import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined';

import Markdown from 'react-markdown';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import { FormRadioGroup, Loader, useTrackedFormik } from '@omnigenbiodata/ui';

import MainLayout from '../../../../layout/Main';
import ROUTES from '../../../../core/constants/routes.constant';
import { CURRENT_PRIVACY_VERSION, CURRENT_TERMS_VERSION } from '../../../../core/constants/content.constant';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { acceptLegalNoticesThunk } from '../../../../store/participant';
import {
  privacyAcceptedSelector,
  termsAcceptedSelector,
  isBusySelector,
} from '../../../../store/participant/selectors';
import { chartColours } from '../../../../core/constants/colours.constants';
import PageContainer from '../../../../components/content/PageContainer';

function CommsScene() {
  const { t } = useTranslation(['comms', 'portal', 'validation']);
  const dispatch = useAppDispatch();

  const isBusy = useAppSelector(isBusySelector);
  const termsAccepted = useAppSelector(termsAcceptedSelector);
  const privacyAccepted = useAppSelector(privacyAcceptedSelector);

  const navigate = useNavigate();

  const validationSchema = yup.object({
    notifyNewProducts: yup.string().required(t('notifyNewProductsRequired', { ns: 'validation' })),
    termsAccepted: yup.string().required('Required'),
    privacyAccepted: yup.string().required('Required'),
  });

  useEffect(() => {
    if (termsAccepted === CURRENT_TERMS_VERSION && privacyAccepted === CURRENT_PRIVACY_VERSION) {
      navigate(ROUTES.portal);
    }
  }, [navigate, termsAccepted, privacyAccepted]);

  const formik = useTrackedFormik(
    {
      initialValues: {
        notifyNewProducts: '',
        termsAccepted: CURRENT_TERMS_VERSION,
        privacyAccepted: CURRENT_PRIVACY_VERSION,
      },
      validationSchema,
      onSubmit: (values) => {
        dispatch(
          acceptLegalNoticesThunk({
            ...values,
            notifyNewProducts: values.notifyNewProducts === 'Y',
          }),
        );
      },
    },
    'consent-comms',
  );

  return (
    <>
      <MainLayout>
        <PageContainer
          title={t('title')}
          icon={
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${chartColours[3]}26`,
                color: chartColours[3],
                flexShrink: 0,
                border: 1,
                borderColor: `${chartColours[3]}55`,
              }}
            >
              <MarkEmailReadOutlined sx={{ fontSize: 34 }} />
            </Box>
          }
        >
          <form onSubmit={formik.handleSubmit}>
            <Box
              sx={{
                mb: 2.5,
                maxWidth: 780,
                color: 'text.secondary',
                '& p': {
                  mt: 0,
                  mb: 1.5,
                  lineHeight: 1.65,
                },
                '& p:last-child': {
                  mb: 0,
                },
                '& ul, & ol': {
                  pl: 3,
                },
                '& li': {
                  mb: 0.75,
                  lineHeight: 1.6,
                },
                '& a': {
                  fontWeight: 900,
                },
                '& strong': {
                  fontWeight: 900,
                },
              }}
            >
              <Markdown>{t('intro')}</Markdown>
            </Box>

            {formik.errors.notifyNewProducts && formik.touched.notifyNewProducts && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  border: 1,
                  borderColor: 'error.light',
                  alignItems: 'center',
                  '& .MuiAlert-message': {
                    fontWeight: 700,
                  },
                }}
              >
                {formik.errors.notifyNewProducts}
              </Alert>
            )}

            <Paper
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'grey.50',
                p: { xs: 2, md: 2.5 },
                '& label': {
                  fontWeight: 800,
                },
              }}
            >
              <FormRadioGroup
                hideLabel
                error={formik.errors.notifyNewProducts}
                name="notifyNewProducts"
                label={t('title')}
                orText={t('options.or')}
                options={[
                  {
                    value: 'Y',
                    label: (t('options.option1') as string) || '',
                  },
                  {
                    value: 'N',
                    label: (t('options.option2') as string) || '',
                  },
                ]}
                onChange={formik.handleChange}
                touched={formik.touched.notifyNewProducts}
                value={formik.values.notifyNewProducts}
              />
            </Paper>

            <Box
              mb={2.5}
              mt={2.5}
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'flex-end',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button
                size="large"
                variant="contained"
                color="primary"
                type="submit"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: 260 },
                  borderRadius: 999,
                  px: 3,
                  py: 1.35,
                  fontWeight: 900,
                  textTransform: 'none',
                  boxShadow: 3,
                }}
              >
                {t('button')}
              </Button>
            </Box>
          </form>
        </PageContainer>
      </MainLayout>

      <Loader isVisible={isBusy} label={t('saving')} />
    </>
  );
}

export default CommsScene;
